export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

export const defaultApiTimeoutMs = 10_000;

export async function api<T>(path: string, init?: RequestInit, options: { timeoutMs?: number } = {}): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  const abortFromCaller = () => controller.abort();
  init?.signal?.addEventListener("abort", abortFromCaller, { once: true });
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, options.timeoutMs ?? defaultApiTimeoutMs);
  try {
    const response = await fetch(`/api${path}`, {
      ...init,
      signal: controller.signal,
      credentials: "include",
      headers: {
        ...(init?.body ? { "Content-Type": "application/json" } : {}),
        ...init?.headers,
      },
    });
    if (!response.ok) {
      const data = (await response.json().catch(() => ({
        error: { code: "HTTP_ERROR", message: response.statusText },
      }))) as { error?: { code?: string; message?: string } };
      throw new ApiError(
        data.error?.code ?? "HTTP_ERROR",
        data.error?.message ?? response.statusText,
        response.status,
      );
    }
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (timedOut) throw new ApiError("API_TIMEOUT", "La API excedió el tiempo de espera", 0);
    if (init?.signal?.aborted) throw new ApiError("API_REQUEST_CANCELLED", "La solicitud fue cancelada", 0);
    throw new ApiError("API_UNREACHABLE", "No fue posible establecer conexión con la API", 0);
  } finally {
    clearTimeout(timer);
    init?.signal?.removeEventListener("abort", abortFromCaller);
  }
}

export const formatDate = (value?: string | null) =>
  value
    ? new Intl.DateTimeFormat("es-PY", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";
